import {
  faPhone,
  faReply,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Image } from "antd";
import Flex from "components/shared-components/Flex";
import React, { useState } from "react";
import { useEffect } from "react";
import { UnorderedListOutlined } from "@ant-design/icons";
import { useTemplates } from "hooks/api/useTemplates";

const applyBodyValues = (bodyText, bodyValues) => {
  if (!bodyText || !Array.isArray(bodyValues) || bodyValues.length === 0) {
    return bodyText;
  }

  return bodyValues.reduce((current, value, index) => {
    const pattern = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
    return current.replace(pattern, String(value ?? ""));
  }, bodyText);
};

const RanderTemplatePreview = ({ text, presetName, bodyValues }) => {
  const { templates } = useTemplates();
  // mobile preview style
  const styles = {
    chatBubble: {
      background: "white",
      borderRadius: "8px",
      padding: "8px",
      position: "relative",
    //   marginLeft: "20px",
      // maxWidth: "85%",
      width: "100%",
      wordWrap: "break-word",
    },
  chatBubbleBefore: {
    content: '""',
    position: "absolute",
    top: "0",
    right: "-5px", // Changed from left: "-5px" to right: "-5px"
    width: "0",
    height: "0",
    borderStyle: "solid",
    borderWidth: "10px 0 10px 10px", // Changed from "10px 10px 10px 0" to "10px 0 10px 10px"
    borderColor: "transparent transparent transparent white", // Changed from "transparent white transparent transparent"
    background: "#ffff",
    backgroundColor: "#ffff",
    clipPath: "polygon(0 0, 100% 0, 0 100%)", // Changed from "polygon(0 0, 0 100%, 100% 100%)"
    transform: "none", // Removed rotation since we're flipping the shape horizontally
  },
    buttoneBubble: {
      background: "white",
      borderRadius: "8px",
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      width: "100%",
      // padding: '16px',
      boxShadow: "0 8px 8px -4px rgba(0, 0, 0, 0.15)",
      position: "relative",
      bottom: "0.5rem",
    //   marginLeft: "20px",
      // maxWidth: "85%",
      wordWrap: "break-word",
    },

    chatTime: {
      textAlign: "right",
      fontSize: "12px",
      color: "gray",
      marginTop: "8px",
    },
    footerMessageStyle: {
      fontSize: "12px",
      marginTop: "5px",
      color: "#918f8f",
    },
    buttonStyle: {
      // fontSize: '11px',
      wordWrap: "break-word",
      fontFamily: "system-ui",
      display: "block",
      textAlign: "justify",
    },
  };
  const [template, setTemplate] = useState();
  const [displayText, setDisplayText] = useState("");
  const [isHovered, setIsHovered] = useState(false);



  const renderTextWithLinks = (text) => {
    // Regex for URLs
    const urlRegex =
      /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?(?:\/\S*)?)/gi;

    // Regex for dynamic variables
    const variableRegex = /\{\{(\d+)\}\}/g;

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: isHovered ? "rgba(232, 247, 237, 1)" : "transparent",
          borderRadius: "5px",
          padding: "2px 4px",
          transition: "background-color 0.3s ease",
        }}
      >
        {text.split("\n").map((line, index) => (
          <React.Fragment key={index}>
            {line
              ?.split(
                /({{[^}]+}}|\b(https?:\/\/|www\.)[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi
              )
              ?.map((part, subIndex) => {
                // Check for URL
                const urlMatch = part?.match(urlRegex);
                if (urlMatch) {
                  const href = part.startsWith("http")
                    ? part
                    : part.startsWith("www.")
                    ? `https://${part}`
                    : `https://www.${part}`;

                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        // color: '#e8f7ed',
                        textDecoration: "underline",
                        opacity: isHovered ? 0.8 : 1,
                      }}
                      key={subIndex}
                    >
                      {part}
                    </a>
                  );
                }

                // Check for dynamic variables
                const variableMatch = part?.match(variableRegex);
                if (variableMatch) {
                  return (
                    <span
                      key={subIndex}
                      style={{
                        backgroundColor: isHovered
                          ? "#22af4b82"
                          : "transparent",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      {part}
                    </span>
                  );
                }

                // Handle bold text
                return part
                  ?.split(/\*([^*]+)\*/g)
                  ?.map((boldPart, boldIndex) => {
                    if (boldIndex % 2 === 1) {
                      return (
                        <span
                          key={boldIndex}
                          style={{
                            fontWeight: "bold",
                            opacity: isHovered ? 0.8 : 1,
                          }}
                        >
                          {boldPart}
                        </span>
                      );
                    }
                    return <span key={boldIndex}>{boldPart}</span>;
                  });
              })}
            <br />
          </React.Fragment>
        ))}
      </div>
    );
  };

  const templateLookupName = presetName || text;

  useEffect(() => {
    if (!templateLookupName || !templates?.length) {
      setTemplate(undefined);
      return;
    }

    const matchedTemplate = templates.find(
      (item) => item?.name === templateLookupName
    );
    const { header, body, footer, buttons } =
      extractTemplateSections(matchedTemplate);
    setTemplate({ header, body, footer, buttons });
  }, [templateLookupName, templates]);

  useEffect(() => {
    setDisplayText(applyBodyValues(template?.body?.text, bodyValues));
  }, [template, bodyValues]);

  const extractTemplateSections = (template) => {
    let header = null,
      body = null,
      footer = null,
      buttons = null;
    template?.components?.forEach((section) => {
      switch (section.type) {
        case "HEADER":
          header = section;
          break;
        case "BODY":
          body = section;
          break;
        case "FOOTER":
          footer = section;
          break;
        case "BUTTONS":
          buttons = section;
          break;
        default:
          // Handle any other section types if needed
          break;
      }
    });
    return { header, body, footer, buttons };
  };

  return (
    <div className="d-flex align-items-center justify-content-center flex-column chat-preview-container" style={{ maxWidth: '20rem', width: '100%' }}>
        <div style={styles.chatBubble}>
          <div style={styles.chatBubbleBefore}></div>
          <>
            {template?.header?.format === "DOCUMENT" ? (
              <div
                className="border rounded d-flex justify-content-center align-items-center mb-3"
                style={{ background: "#ccd0d5" }}
              >
                <img
                  src={"/img/pdfBackground.png"}
                  className="my-3"
                  width={70}
                  alt="pdf"
                />
              </div>
            ) : template?.header?.format === "IMAGE" ? (
              <Image src={"/img/others/dummy-header.jpg"} className="mt-1" />
            ) : template?.header && template?.header?.format === "VIDEO" ? (
              <div
                className=" d-flex justify-content-center align-items-center mb-3"
                style={{ background: "" }}
              >
                <img
                  src={"/img/dummyvideo.png"}
                  className="mt-1 border rounded"
                  alt="video"
                  width={"100%"}
                />
              </div>
            ) : template?.header?.format === "LOCATION" ? (
              <img
                src={"/img/location.png"}
                className="rounded mb-2"
                alt="location"
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <div className="header">
                <h5>{template?.header?.text}</h5>
              </div>
            )}
            {displayText && (
              <div className="mt-1">{renderTextWithLinks(displayText)}</div>
            )}
            <span style={styles.footerMessageStyle}>
              {template?.footer?.text ? template.footer.text : null}
            </span>
          </>
          {/* <div style={styles.chatTime}>{currentTime}</div> */}
        </div>
        <div style={styles.buttoneBubble}>
          {template?.buttons?.buttons?.length > 0 &&
            template?.buttons?.buttons?.map((item, index) => {
              // Display the first 2 QUICK_REPLY buttons and all other buttons
              if (item?.type === "QUICK_REPLY" && index < 2) {
                return (
                  <span
                    style={styles.buttonStyle}
                    className="border-top py-2"
                    key={index}
                  >
                    <button
                      className="border-0 bg-transparent w-100"
                      style={{ fontWeight: "bold", color: "#00a5f4" }}
                    >
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        gap="5px"
                      >
                        <FontAwesomeIcon icon={faReply} />
                        <span>{item?.text}</span>
                      </Flex>
                    </button>
                  </span>
                );
              } else if (item?.type !== "QUICK_REPLY") {
                return (
                  <span
                    style={styles.buttonStyle}
                    className="border-top py-2"
                    key={index}
                  >
                    {item?.type === "URL" ? (
                      <a
                        href={item?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border-0 bg-transparent w-100 d-block text-center"
                        style={{
                          fontWeight: "bold",
                          color: "#00a5f4",
                          textDecoration: "none",
                        }}
                      >
                        <Flex
                          justifyContent="center"
                          alignItems="center"
                          gap="5px"
                        >
                          <FontAwesomeIcon icon={faUpRightFromSquare} />
                          <span>{item?.text}</span>
                        </Flex>
                      </a>
                    ) : item?.type === "PHONE_NUMBER" ? (
                      <a
                        href={`tel:${item?.phone_number}`}
                        className="border-0 bg-transparent w-100 d-block text-center"
                        style={{
                          fontWeight: "bold",
                          color: "#00a5f4",
                          textDecoration: "none",
                        }}
                      >
                        <Flex
                          justifyContent="center"
                          alignItems="center"
                          gap="5px"
                        >
                          <FontAwesomeIcon icon={faPhone} />
                          <span>{item?.text}</span>
                        </Flex>
                      </a>
                    ) : null}
                  </span>
                );
              }
              return null;
            })}

          {template?.buttons?.buttons?.filter(
            (item) => item?.type === "QUICK_REPLY"
          ).length > 3 && (
            <span style={styles.buttonStyle} className="border-top py-2">
              <button
                className="border-0 bg-transparent w-100"
                style={{ fontWeight: "bold", color: "#00a5f4" }}
              >
                <Flex justifyContent="center" alignItems="center" gap="5px">
                  <UnorderedListOutlined />
                  See all options
                </Flex>
              </button>
            </span>
          )}
        </div>
    </div>
  );
};

export default RanderTemplatePreview;
